/**
 * CallRefreshGuard — registers a `beforeunload` handler whenever a call is
 * active so the browser surfaces its native "Leave site?" confirmation if
 * the agent refreshes, closes the tab, or navigates to an external URL.
 *
 * Renders nothing; it's purely a side-effect component. Mounted at app root
 * by CallLeadPopupProvider so the guard exists app-wide without each route
 * having to wire it up.
 *
 * Notes on browser behavior:
 *   - Modern browsers (Chrome, Firefox, Safari, Edge) IGNORE the custom
 *     `returnValue` string and show their own generic dialog. We still
 *     have to set `returnValue` (and / or call `preventDefault()`) to
 *     trigger that dialog at all.
 *   - This does NOT block React Router's in-app navigation. If we want to
 *     warn on `<Link>` clicks too, that's a separate hook
 *     (`useCallNavigationBlock` exists already for the Zoom flow).
 */
import { useEffect } from "react";
import { useTwilioContext } from "@/context/twilio.context";

const CallRefreshGuard = () => {
  const { isCallActive } = useTwilioContext();

  useEffect(() => {
    if (!isCallActive) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chromium-based browsers honor an explicit returnValue assignment;
      // safer to set both than rely on preventDefault() alone.
      e.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isCallActive]);

  return null;
};

export default CallRefreshGuard;
